/**
 * Resolução de overrides mobile (layout linha/coluna) para palco e export.
 * @package gepetto-frontend
 */

import { BREAKPOINT_MOBILE_MAX, EDIT_BREAKPOINT_MOBILE } from '../constants/responsiveBreakpoints';
import { MOBILE_LAYOUT_KEYS } from './responsiveTypography';

function box4(raw) {
  if (!raw || typeof raw !== 'object') return null;
  return {
    top: parseInt(raw.top, 10) || 0,
    right: parseInt(raw.right, 10) || 0,
    bottom: parseInt(raw.bottom, 10) || 0,
    left: parseInt(raw.left, 10) || 0,
  };
}

function boxesEqual(a, b) {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return a.top === b.top && a.right === b.right && a.bottom === b.bottom && a.left === b.left;
}

/** Migra campos legados da coluna para `responsive.mobile` (somente leitura). */
export function migrateColumnConfigResponsive(raw) {
  const c = raw && typeof raw === 'object' ? { ...raw } : {};
  const responsive = { ...(c.responsive || {}) };
  const mobile = { ...(responsive.mobile || {}) };

  if (mobile.orderSmall === undefined && (parseInt(c.orderSmall, 10) || 0) > 0) {
    mobile.orderSmall = parseInt(c.orderSmall, 10) || 0;
  }
  if (mobile.centerOnSmall === undefined && c.centerOnSmall) {
    mobile.centerOnSmall = true;
  }
  if (mobile.paddingTop === undefined && (parseInt(c.paddingTop, 10) || 0) > 0) {
    mobile.paddingTop = parseInt(c.paddingTop, 10) || 0;
  }
  if (mobile.paddingBottom === undefined && (parseInt(c.paddingBottom, 10) || 0) > 0) {
    mobile.paddingBottom = parseInt(c.paddingBottom, 10) || 0;
  }

  if (Object.keys(mobile).length === 0) {
    return c;
  }
  return { ...c, responsive: { ...responsive, mobile } };
}

export function getColumnMobileOverrides(cc) {
  const migrated = migrateColumnConfigResponsive(cc || {});
  return migrated.responsive?.mobile || {};
}

export function hasMobileLayoutOverrides(config) {
  const m = config?.responsive?.mobile;
  if (!m || typeof m !== 'object') return false;
  return MOBILE_LAYOUT_KEYS.some((k) => {
    const v = m[k];
    if (v === undefined || v === null || v === false || v === 0) return false;
    if (typeof v === 'object' && !Array.isArray(v)) {
      return Object.values(v).some((n) => n !== 0 && n !== undefined && n !== null);
    }
    return true;
  });
}

export function clearMobileLayoutOverrides(content) {
  const mobile = { ...(content?.responsive?.mobile || {}) };
  MOBILE_LAYOUT_KEYS.forEach((k) => { delete mobile[k]; });
  const responsive = { ...(content?.responsive || {}) };
  if (Object.keys(mobile).length === 0) delete responsive.mobile;
  else responsive.mobile = mobile;
  return { ...content, responsive };
}

/** Classes responsivas da coluna (export + palco). */
export function columnResponsiveClassNames(cc) {
  const mobile = getColumnMobileOverrides(cc);
  const classes = [];
  if (mobile.centerOnSmall) classes.push('madri-col-sm-center');
  const order = Math.max(0, Math.min(12, parseInt(mobile.orderSmall, 10) || 0));
  if (order > 0) classes.push(`madri-col-sm-order-${order}`);
  return classes;
}

/** Config efetiva da linha no palco conforme breakpoint de edição. */
export function resolveRowConfigForEdit(rc, editBreakpoint) {
  const base = rc && typeof rc === 'object' ? rc : {};
  if (editBreakpoint !== EDIT_BREAKPOINT_MOBILE) return base;
  const mobile = base.responsive?.mobile;
  const out = { ...base };
  if (mobile?.marginObj) {
    out.marginObj = { ...(base.marginObj || {}), ...box4(mobile.marginObj) };
  }
  if (mobile?.paddingObj) {
    out.paddingObj = { ...(base.paddingObj || {}), ...box4(mobile.paddingObj) };
  } else if (base.paddingObj) {
    const pt = base.paddingObj.top || 0;
    const pr = base.paddingObj.right || 0;
    const pb = base.paddingObj.bottom || 0;
    const pl = base.paddingObj.left || 0;
    out.paddingObj = {
      ...base.paddingObj,
      top: Math.min(pt, 48),
      right: Math.min(pr, 12),
      bottom: Math.min(pb, 48),
      left: Math.min(pl, 12),
    };
  }
  return out;
}

/** Padding/margin mobile no export: overrides explícitos ou clamp legado da linha. */
export function pushRowMobileLayoutRules(layoutStyleRules, rowId, rc) {
  const mobile = rc?.responsive?.mobile;
  const rowSel = `.madri-moodle-layout .madri-row-${rowId}`;
  const mq = `@media (max-width: ${BREAKPOINT_MOBILE_MAX}px)`;

  if (mobile?.marginObj) {
    const m = box4(mobile.marginObj);
    if (m) {
      layoutStyleRules.push(`${mq} { ${rowSel} { margin-top: ${m.top}px !important; margin-right: ${m.right}px !important; margin-bottom: ${m.bottom}px !important; margin-left: ${m.left}px !important; } }`);
    }
  }

  if (mobile?.paddingObj) {
    const p = box4(mobile.paddingObj);
    if (p) {
      layoutStyleRules.push(`${mq} { ${rowSel} { padding-top: ${p.top}px !important; padding-bottom: ${p.bottom}px !important; padding-left: ${p.left}px !important; padding-right: ${p.right}px !important; } }`);
      return;
    }
  }

  if (!rc?.paddingObj || mobile?.paddingObj) return;
  const pt = rc.paddingObj.top || 0;
  const pr = rc.paddingObj.right || 0;
  const pb = rc.paddingObj.bottom || 0;
  const pl = rc.paddingObj.left || 0;
  const mobPt = Math.min(pt, 48);
  const mobPb = Math.min(pb, 48);
  const mobPr = Math.min(pr, 12);
  const mobPl = Math.min(pl, 12);
  if (!boxesEqual({ top: pt, right: pr, bottom: pb, left: pl }, { top: mobPt, right: mobPr, bottom: mobPb, left: mobPl })) {
    layoutStyleRules.push(`${mq} { ${rowSel} { padding-top: ${mobPt}px !important; padding-bottom: ${mobPb}px !important; padding-left: ${mobPl}px !important; padding-right: ${mobPr}px !important; } }`);
  }
}

/** Regras mobile de padding, centralização e ordem da coluna. */
export function pushColumnMobileLayoutRules(layoutStyleRules, rowId, colId, cc) {
  const mobile = getColumnMobileOverrides(cc);
  const colDirect = `.madri-moodle-layout .madri-row-${rowId} > .madri-col-${colId}`;
  const colInner = `.madri-moodle-layout .madri-row-${rowId} > .madri-row-inner > .madri-col-${colId}`;
  const colSel = `${colDirect}, ${colInner}`;
  const mq = `@media (max-width: ${BREAKPOINT_MOBILE_MAX}px)`;
  const parts = [];
  if (mobile.paddingTop > 0) parts.push(`padding-top: ${mobile.paddingTop}px !important`);
  if (mobile.paddingBottom > 0) parts.push(`padding-bottom: ${mobile.paddingBottom}px !important`);
  if (mobile.centerOnSmall) {
    parts.push(
      'display: flex !important',
      'flex-direction: column !important',
      'align-items: center !important',
      'text-align: center !important',
    );
  }
  const order = Math.max(0, Math.min(12, parseInt(mobile.orderSmall, 10) || 0));
  if (order > 0) parts.push(`order: ${order} !important`);
  if (parts.length) {
    layoutStyleRules.push(`${mq} { ${colSel} { ${parts.join('; ')}; } }`);
  }
  if (mobile.centerOnSmall) {
    layoutStyleRules.push(
      `${mq} { ${colDirect} [class*="madri-widget-"], ${colDirect} .widget-block, ${colInner} [class*="madri-widget-"], ${colInner} .widget-block { text-align: center !important; } }`,
    );
  }
}

export function patchResponsiveMobile(content, patch) {
  const responsive = { ...(content?.responsive || {}) };
  const mobile = { ...(responsive.mobile || {}) };
  Object.entries(patch).forEach(([key, val]) => {
    if ((key === 'centerOnSmall' || key === 'hideOnMobile' || key === 'centerOnMobile') && !val) {
      delete mobile[key];
    } else {
      mobile[key] = val;
    }
  });
  const nextResponsive = { ...responsive };
  if (Object.keys(mobile).length === 0) {
    delete nextResponsive.mobile;
  } else {
    nextResponsive.mobile = mobile;
  }
  const out = { ...content, responsive: Object.keys(nextResponsive).length ? nextResponsive : undefined };
  if (Object.prototype.hasOwnProperty.call(patch, 'centerOnSmall')) {
    out.centerOnSmall = !!patch.centerOnSmall;
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'orderSmall')) {
    out.orderSmall = parseInt(patch.orderSmall, 10) || 0;
  }
  return out;
}

export function patchResponsiveMobileBox(content, boxKey, side, val) {
  const responsive = { ...(content?.responsive || {}) };
  const mobile = { ...(responsive.mobile || {}) };
  const box = { ...(mobile[boxKey] || {}), [side]: parseInt(val, 10) || 0 };
  return patchResponsiveMobile(content, { [boxKey]: box });
}

/** Some no celular (≤767px) quando o autor marcou ocultar. */
export function isHiddenOnMobile(config) {
  return !!(config?.responsive?.mobile?.hideOnMobile);
}

export const HIDE_ON_MOBILE_CLASS = 'madri-hide-mobile';

function floatingPlacement(fi) {
  const source = fi || {};
  const op = (source.opacity != null ? source.opacity : 100) / 100;
  const rot = source.rotate != null ? source.rotate : 0;
  const sz = (source.size != null ? source.size : 100) / 100;
  const corner = source.positionCorner || 'left-top';
  const baseX = corner === 'center' ? 50 : (String(corner).includes('right') ? 100 : 0);
  const baseY = corner === 'center' ? 50 : (String(corner).includes('bottom') ? 100 : 0);
  const posX = (baseX + (source.positionX != null ? source.positionX : 0)) + '%';
  const posY = (baseY + (source.positionY != null ? source.positionY : 0)) + '%';
  const layer = source.layer != null ? source.layer : 0;
  return { op, rot, sz, posX, posY, layer };
}

/** No palco mobile, aplica os ajustes da imagem flutuante. `hidden` esconde. */
export function resolveFloatingImage(fi, useMobile) {
  const base = fi && typeof fi === 'object' ? fi : {};
  if (!useMobile) return base;
  const mobile = base.mobile || {};
  if (mobile.hidden) return { ...base, hidden: true };
  const pick = (key) => (mobile[key] != null && mobile[key] !== '' ? mobile[key] : base[key]);
  return {
    ...base,
    opacity: pick('opacity'),
    rotate: pick('rotate'),
    size: pick('size'),
    positionCorner: pick('positionCorner'),
    positionX: pick('positionX'),
    positionY: pick('positionY'),
    layer: pick('layer'),
  };
}

function pushFloatingImageMobileRules(layoutStyleRules, rowId, fi) {
  const mobile = fi?.mobile;
  if (!mobile || typeof mobile !== 'object' || !Object.keys(mobile).length) return;
  const sel = `.madri-moodle-layout .madri-row-${rowId} .madri-row-float`;
  const mq = `@media (max-width: ${BREAKPOINT_MOBILE_MAX}px)`;
  if (mobile.hidden) {
    layoutStyleRules.push(`${mq} { ${sel} { display: none !important; } }`);
    return;
  }
  const place = floatingPlacement(resolveFloatingImage(fi, true));
  layoutStyleRules.push(`${mq} { ${sel} { z-index: ${place.layer} !important; } }`);
  layoutStyleRules.push(`${mq} { ${sel} img { opacity: ${place.op} !important; object-position: ${place.posX} ${place.posY} !important; transform: rotate(${place.rot}deg) scale(${place.sz}) !important; transform-origin: ${place.posX} ${place.posY} !important; } }`);
}

/** HTML da imagem flutuante + regras mobile, igual na visualização e no arquivo publicado. */
export function buildRowFloatingImageHtml(rc, rowId, layoutStyleRules) {
  const fi = rc?.floatingImage || {};
  const fiUrl = String(fi.imageUrl || '').trim();
  if (!fiUrl) return '';
  if (layoutStyleRules) pushFloatingImageMobileRules(layoutStyleRules, rowId, fi);
  const place = floatingPlacement(fi);
  const src = fiUrl.replace(/"/g, '&quot;');
  return `<div class="madri-row-float" style="position:absolute;inset:0;z-index:${place.layer};overflow:hidden;pointer-events:none"><img src="${src}" alt="" style="width:100%;height:100%;object-fit:contain;object-position:${place.posX} ${place.posY};opacity:${place.op};transform:rotate(${place.rot}deg) scale(${place.sz});transform-origin:${place.posX} ${place.posY}" /></div>`;
}
